<template>
  <div id="app">
    <div class="container" v-if="selectedRegion">  
      <IndicatorBox
        :title="selectedRegion"
        :indicators="indicators"
        :notes="notes"
      />
    </div>
    <ControlGroup>
      <DataGetter
        :url="`${apiRoute}/list/Country`"
        :params="{ parameter: parameter.parameter }"
        v-slot="{ data }"
        :child-element-type="null"
        v-if="!selectedRegion"
      >
        <Dropdown
          key="regionsdd"
          label="Country"
          v-model="region"
          :options="[
            { label: 'Select a country', value: null },
            ...(data || [])
          ]"
        />
      </DataGetter>
      <Dropdown
        key="parameterdd"
        label="Show sales by"
        v-model="parameter"
        :options="parameters.map(param => ({ label: param.label, value: param }))"
      />
      <Dropdown
        key="fuelconsparam"
        label="Show fuel consumption by"
        v-model="fuelCons"
        :options="[
          'Powertrain',
          'Segment'
        ]"
      />
    </ControlGroup>
    <div class="container" v-if="region">
      <div class="m-block m-block--charts m-block--charts-half">
        
      <DataGetter
        :key="`datagetter${region}fuelcons`"
        :url="`${apiRoute}/${region}/Fuel consumption`"
        v-slot="{ data, isLoading }"
      >
        <DataSlicer
          :data="[...data]"
          x-axis-dimension="Year"
          y-axis-dimension="Product"
          value-dimension="Value"
          :filters="{
            Product: fuelConsProducts
          }"
          v-slot="{ slicedData }"
          class="m-chart-block"
          key="chartslicerfc"
          v-if="!isLoading"
        >
          <header>
            <span class="f-ui-2-bold">
              Fuel consumption by {{ fuelCons.toLowerCase() }}, 2005-2019
            </span>
          </header>
          <div class="m-chart__item">
            <p class="m-chart__units">lge/100 km</p>
            <Chart
              :key="`${region}fuelconschart`"
              :options="{
                ...chartOptions,
                chart: {
                  styledMode: true,
                  type: 'line',
                  height: 400
                },
                tooltip: {
                  valueDecimals: 1,
                  valueSuffix: ' lge/100 km'
                },
                series: slicedData.map(series => 
                  fuelConsOrder[fuelCons].includes(series.name)
                    ? { ...series, colorIndex: fuelConsOrder[fuelCons].indexOf(series.name), legendIndex: fuelConsOrder[fuelCons].indexOf(series.name) }
                    : series
                )
              }"
              class="highcharts-outline-black"
            />
            <small class="m-chart__credit f-ui-3">
              IEA. All rights reserved.
            </small>
          </div>
        </DataSlicer>
      </DataGetter>
      <DataGetter
        :key="`datagetter${region}${parameter.parameter}`"
        :url="`${apiRoute}/${region}/${parameter.parameter}`"
        v-slot="{ data, isLoading }"
      >
        <DataSlicer
          :data="[...data]"
          x-axis-dimension="Year"
          y-axis-dimension="Product"
          value-dimension="Value"
          class="m-chart-block"
          key="chartslicer"
          v-if="!isLoading"
          v-slot="{ slicedData }"
        >
          <header>
            <span class="f-ui-2-bold">
              Sales by {{ parameter.label.toLowerCase() }}<span v-if="parameter.units"> ({{ parameter.units }})</span>, 2005-2019
            </span>
          </header>
          <div class="m-chart__item">
            <p class="m-chart__units">%</p>
            <Chart
              :key="`${region}${parameter.parameter}chart`"
              :options="{
                ...chartOptions,
                chart: {
                  styledMode: true,
                  type: 'column',
                  height: 400
                },
                tooltip: {
                  pointFormat: `${parameter.label !== 'Powertrain' ? parameter.label : ''} {series.name} ${parameter.units || ''}: <b>{point.percentage:0.1f}%</b>`
                },
                yAxis: parameter.units ? [
                  {
                    title: {
                      text: ''
                    },
                    reversedStacks: false
                  },
                  {
                    opposite: true,
                    min: 0,
                    title: {
                      text: `Average (${parameter.units})`
                    }
                  }
                ] : {},
                series: slicedData.map(row => ({ ...row })).sort((a,b) => {
                  if (a.name === 'Average') return -1;
                  if (b.name === 'Average') return 1;
                  if (a.name[0] === '>' || b.name[0] === '<') return 1;
                  if (a.name[0] === '<' || b.name[0] === '>') return -1;
                  return a.name.split('-')[0] - b.name.split('-')[0];
                }).map(series => series.name === 'Average'
                  ? { ...series, yAxis: 1, type: 'line', zIndex: 999, tooltip: { pointFormat: `Average ${parameter.label.toLowerCase()}: <b>{point.y:0.1f} ${parameter.units || ''}</b>` } }
                  : { ...series })
              }"
              class="highcharts-outline-black"
            />
            <small class="m-chart__credit f-ui-3">
              IEA. All rights reserved.
            </small>
          </div>
        </DataSlicer>
      </DataGetter>
      </div>
    </div>
  </div>
</template>

<script>
/* global Highcharts */
import { Chart } from 'highcharts-vue';
import { DataGetter, DataSlicer, ControlGroup, Dropdown, IndicatorBox } from '@international-energy-agency/vue-ui';
import ChartOptions from '@international-energy-agency/vue-ui/highcharts';
import countryData from './countryData.json';
Highcharts.setOptions({
  lang: {
     numericSymbols: null
  }
});
const parameters = [
  {
    label: 'Footprint',
    parameter: 'FootprintReduced',
    units: 'm2'
  },
  {
    label: 'Engine displacement',
    parameter: 'EngineDisplaceReduced',
    units: 'cm3'
  },
  {
    label: 'Engine power',
    parameter: 'EnginePowerReduced',
    units: 'kW'
  },
  {
    label: 'Powertrain',
    parameter: 'FuelTypeReduced',
    units: null
  },
  {
    label: 'Kerb weight',
    parameter: 'KerbweightReduced',
    units: 'kg'
  },
  {
    label: 'CO2 emissions class',
    parameter: 'NormCO2_NEDC_Reduced',
    units: 'gCO2/km'
  }
];
export default {
  name: 'App',
  components: {
    DataGetter,
    DataSlicer,
    Chart,
    ControlGroup,
    Dropdown,
    IndicatorBox
  },
  props: {
    selectedRegion: {
      type: String,
      default: null
    }
  },
  data() {
    return {
      apiRoute: 'https://api.iea.org/gfei',
      region: this.selectedRegion || null,
      regions: [],
      parameter: parameters[0],
      parameters,
      units: '',
      fuelCons: 'Powertrain',
      fuelConsOrder: {
        'Powertrain': [
          'All vehicles',
          'Diesel',
          'Petrol',
          'Flexfuel',
          'CNG',
          'LPG',
          'Hybrid',
          'Electric',
          'Plug-in'
        ],
        'Segment': [
          'All vehicles',
          'City car',
          'Medium car',
          'Large car',
          'Small SUV/Pick-up',
          'Large SUV/Pick-up',
          'Van/LCV'
        ]
      },
      chartOptions: ChartOptions.buildOptions({
        plotOptions: {
          column: {
            stacking: 'percent'
          }
        },
        xAxis: {
          tickInterval: 1,
          showLastLabel: true,
          showFirstLabel: true
        }
      }, 'column')
    }
  },
  computed: {
    notes() {
      const country = countryData[this.selectedRegion];
      return `PPP = purchasing power parity. Urban population share from World Bank: https://databank.worldbank.org/reports.aspx?source=health-nutrition-and-population-statistics
        ${country.gasPriceGIZ ? 'Gasoline price from GIZ International Fuel Prices.' : ''}`
    },
    indicators() {
      const country = countryData[this.selectedRegion];
      if (!country) return {};
      return {
        'Population|2021 million': country.population,
        'Share of urban population|2020': `${country.urbanShare}%`,
        'GDP|PPP per capita, thousand 2019 USD': country.gdpPerCapita,
        'Gasoline price|2019 USD PPP/litre': country.gasPrice,
      };
    },
    fuelConsProducts() {
      const { fuelCons } = this;
      return fuelCons === 'Powertrain'
        ? [
          'Petrol',
          'Diesel',
          'Electric',
          'Flexfuel',
          'Hybrid',
          'LPG',
          'Plug-in',
          'All vehicles'
        ]
        : [
          'City car',
          'Medium car',
          'Small SUV/Pick-up',
          'Large car',
          'Large SUV/Pick-up',
          'All vehicles'
        ]
    }
  },
  methods: {
    setUnits(units) {
      this.units = units;
    }
  }
}
</script>
